import cx from "classnames";
import { useCallback, useContext, useMemo, useState } from "react";
import { ComboBoxStateContext } from "react-aria-components";

import Tag from "ds/components/Tag";
import Box from "ds/components/Box";
import AriaInput, { AriaInputProps } from "ds/components/Input/AriaInput";
import ComboBoxClearButton from "ds/components/ComboBox/ClearButton";
import ComboBoxOpenButton from "ds/components/ComboBox/OpenButton";
import LoadingIndicator from "ds/components/LoadingIndicator";
import useElementWidth from "hooks/useElementWidth";
import useOverflowItems from "hooks/useOverflowItems";
import { BaseCollection, SectionBaseCollection } from "ds/components/ComboBox/types";

import styles from "./styles.module.css";
import TagValue from "./TagValue";

const MIN_INPUT_SIZE = 200;
const SPACE = 4;
const SPACE_FOR_PLUS_ITEM = 50;

export type ComboBoxMultipleInputProps<
  Collection extends BaseCollection | SectionBaseCollection<BaseCollection, SectionProps>,
  SectionProps extends object = object,
> = {
  id: string;
  size: AriaInputProps["size"];
  color: AriaInputProps["color"];
  placeholder?: string;
  isDisabled?: boolean;
  isError?: boolean;
  isLoading?: boolean;
  values?: string[] | null;
  items: Array<Collection>;
};

const ComboBoxMultipleInput = <
  Collection extends BaseCollection | SectionBaseCollection<BaseCollection, SectionProps>,
  SectionProps extends object = object,
>({
  id,
  placeholder,
  isDisabled,
  isError,
  isLoading,
  size,
  color,
  values = [],
  items,
}: ComboBoxMultipleInputProps<Collection, SectionProps>) => {
  const state = useContext(ComboBoxStateContext);
  const [tagsSizes, setTagsSizes] = useState<Record<string, number>>({});
  const [wrapperRef, wrapperSize] = useElementWidth<HTMLDivElement>();
  const [tagsWrapperRef, tagsWrapperSize] = useElementWidth<HTMLDivElement>();

  const getItemWidth = useCallback((tag: string) => tagsSizes[tag] || 0, [tagsSizes]);
  const effectiveContainerWidth = wrapperSize ? wrapperSize - MIN_INPUT_SIZE : 0;

  const { hiddenItems: hiddenTags } = useOverflowItems({
    items: values || [],
    containerWidth: effectiveContainerWidth,
    getItemWidth,
    overflowTriggerWidth: SPACE_FOR_PLUS_ITEM,
    gap: SPACE,
  });

  const tagsToRender = useMemo(() => {
    return values?.reduce(
      (acc, next) => {
        if (hiddenTags.includes(next)) {
          return acc;
        }

        const label = items.find((item) => "value" in item && item.value === next)?.label;

        if (!label) {
          return acc;
        }

        return [...acc, { label, value: next }];
      },
      [] as Array<{ label: string; value: string }>
    );
  }, [values, hiddenTags, items]);

  return (
    <Box ref={wrapperRef} className={styles.inputWrapper} data-values={values?.length} fullWidth>
      <Box ref={tagsWrapperRef} className={styles.tags} gap="small">
        {tagsToRender?.map(({ value, label }, i) => (
          <TagValue
            setTagSizes={setTagsSizes}
            currentSize={tagsSizes[value]}
            key={i}
            id={value}
            tag={label}
            containerSize={tagsWrapperSize}
            onRemove={() => state?.setSelectedKey(value)}
          />
        ))}
        {!!hiddenTags.length && <Tag tag={`+${hiddenTags.length}`} />}
      </Box>
      <AriaInput
        id={id}
        style={{ paddingLeft: `${12 + tagsWrapperSize}px` }}
        className={cx(styles.input, {
          [styles.withClear]: state?.inputValue !== "",
          [styles.withLoading]: isLoading,
        })}
        disabled={isDisabled}
        error={isError}
        placeholder={placeholder}
        size={size}
        color={color}
      />
      <Box className={styles.buttonWrapper} gap="small" align="center">
        {isLoading && <LoadingIndicator loading />}
        <ComboBoxClearButton isDisabled={isDisabled} />
        <ComboBoxOpenButton isDisabled={isDisabled} />
      </Box>
    </Box>
  );
};

ComboBoxMultipleInput.displayName = "DS.ComboBox.Input";

export default ComboBoxMultipleInput;
